/**
 * AuthContext (Solid.js)
 * Authentifizierungs-Context für die gesamte App
 */

import { createContext, useContext, createSignal, onMount, onCleanup, type JSX } from 'solid-js';
import type { User, LoginCredentials, RegisterData, AuthResponse } from '../types/user.types';
import apiClient from '../utils/api';
import logger from '../utils/logger';
import { storage } from '../utils/storage';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  isSupporter: boolean;
  isBetaTester: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  refreshAuth: () => Promise<void>;
  refreshUser: () => Promise<void>;
  updateUser: (userData: Partial<User>) => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType>();

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth muss innerhalb eines AuthProvider verwendet werden');
  return ctx;
};

export function AuthProvider(props: { children: JSX.Element }) {
  const [user, setUser] = createSignal<User | null>(null);
  // Beim Neuladen: Wenn ein Token im localStorage liegt, erst Session prüfen – sonst sofort "nicht eingeloggt"
  const hasStoredToken = !!storage.getItem('token');
  const [isLoading, setIsLoading] = createSignal(hasStoredToken);

  /** Backend liefert das Profilbild je nach Route als profile_image_path (snake_case). */
  const withAvatar = (u: User): User => {
    const raw = u as User & { profile_image_path?: string | null };
    return { ...u, profileImagePath: u.profileImagePath ?? raw.profile_image_path ?? undefined };
  };

  const refreshAuth = async (): Promise<void> => {
    try {
      const response = await apiClient.post<{ success: boolean; data: { user: User } }>('/auth/session');
      const body = response.data;
      if (body?.success && body?.data?.user) {
        const u = body.data.user;
        setUser(withAvatar({ ...u, id: String(u.id) }));
      } else {
        throw new Error('Session ungültig');
      }
    } catch (error) {
      const refreshToken = storage.getItem('refreshToken');
      if (refreshToken) {
        try {
          const refreshResponse = await apiClient.post<{ success: boolean; data: { token: string; user: User } }>('/auth/refresh', { refreshToken });
          const refBody = refreshResponse.data;
          if (refBody?.success && refBody?.data) {
            apiClient.setToken(refBody.data.token);
            storage.setItem('token', refBody.data.token);
            const u = refBody.data.user;
            setUser(withAvatar({ ...u, id: String(u.id) }));
            return;
          }
        } catch {
          /* refresh fehlgeschlagen */
        }
      }
      apiClient.clearToken();
      storage.removeItem('token');
      storage.removeItem('refreshToken');
      setUser(null);
      throw error;
    }
  };

  const logout = (): void => {
    apiClient.clearToken();
    storage.removeItem('token');
    storage.removeItem('refreshToken');
    setUser(null);
    logger.info('Logout erfolgreich');
  };

  onMount(() => {
    const handleUnauthorized = () => {
      if (user()) {
        logout();
      }
    };
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    // Anderer Tab hat sich mit einem anderen Konto angemeldet/abgemeldet: Dieser Tab würde sonst mit dem Token
    // des neuen Kontos weiterarbeiten, aber noch das alte Konto anzeigen (Folge: 401/403-Fehler im Hintergrund).
    const tokenUserId = (tok: string | null): string | null => {
      try {
        if (!tok) return null;
        const payload = JSON.parse(atob(tok.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        return payload?.id ? String(payload.id) : null;
      } catch { return null; }
    };
    const handleStorage = (e: StorageEvent) => {
      if (e.key !== 'token' || !user()) return;
      const newId = tokenUserId(e.newValue);
      if (!e.newValue || (newId && newId !== String(user()!.id))) window.location.reload();
    };
    window.addEventListener('storage', handleStorage);
    onCleanup(() => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
      window.removeEventListener('storage', handleStorage);
    });
    // Kürzere Timeouts: Bei manuellem Reload nicht ewig warten (Server langsam/Netzwerk)
    const AUTH_INIT_TIMEOUT_MS = 5000;  // Session-Check max 5s
    const AUTH_SAFETY_MS = 8000;        // Spinner auf jeden Fall nach 8s beenden (wird nie gecleart)

    // Sicherheits-Timeout: Spinner nach 8s auf jeden Fall beenden (Timer läuft bewusst weiter als Fallback)
    setTimeout(() => setIsLoading(false), AUTH_SAFETY_MS);

    const init = async () => {
      try {
        const token = storage.getItem('token');
        if (token) {
          apiClient.setToken(token);
          try {
            await Promise.race([
              refreshAuth(),
              new Promise<void>((_, reject) =>
                setTimeout(() => reject(new Error('AUTH_TIMEOUT')), AUTH_INIT_TIMEOUT_MS)
              ),
            ]);
          } catch (err: any) {
            if (err?.message === 'AUTH_TIMEOUT') {
              logger.warn('Auth-Session-Check Zeitüberschreitung');
            }
            apiClient.clearToken();
            storage.removeItem('token');
            storage.removeItem('refreshToken');
            setUser(null);
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    init().catch(err => {
      logger.error('Auth init error:', err);
      setIsLoading(false);
    });
  });

  const login = async (credentials: LoginCredentials): Promise<void> => {
    const response = await apiClient.post<{ success: boolean; data: AuthResponse; error?: { message?: string } }>('/auth/login', credentials);
    const body = response.data;
    if (body?.success && body?.data) {
      const { user: u, token, refreshToken } = body.data;
      apiClient.setToken(token);
      storage.setItem('token', token);
      if (refreshToken) storage.setItem('refreshToken', refreshToken);
      setUser(withAvatar(u));
      logger.info('Login erfolgreich', { username: u.username });
    } else {
      const msg = body?.error?.message || 'Login fehlgeschlagen';
      throw Object.assign(new Error(msg), { error: body?.error });
    }
  };

  const register = async (data: RegisterData): Promise<void> => {
    const response = await apiClient.post<{ success: boolean; data: AuthResponse; error?: { message?: string } }>('/auth/register', data);
    const body = response.data;
    if (body?.success && body?.data) {
      const { user: u, token, refreshToken } = body.data;
      apiClient.setToken(token);
      storage.setItem('token', token);
      if (refreshToken) storage.setItem('refreshToken', refreshToken);
      setUser(withAvatar(u));
      logger.info('Registrierung erfolgreich', { username: u.username });
    } else {
      const msg = body?.error?.message || 'Registrierung fehlgeschlagen';
      throw Object.assign(new Error(msg), { error: body?.error });
    }
  };

  const updateUser = (userData: Partial<User>): void => {
    const u = user();
    if (u) setUser({ ...u, ...userData });
  };

  const value: AuthContextType = {
    get user() { return user(); },
    get isAuthenticated() { return !!user(); },
    get isLoading() { return isLoading(); },
    get loading() { return isLoading(); },
    get isAdmin() { return user()?.role === 'admin'; },
    get isSupporter() { return user()?.role === 'supporter' || user()?.role === 'admin'; },
    get isBetaTester() { return user()?.role === 'beta_tester'; },
    login,
    register,
    logout,
    refreshAuth,
    refreshUser: refreshAuth,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {props.children}
    </AuthContext.Provider>
  );
}
